import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentService, UserService } from '../../../../../shared/services';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { atLeastOne } from '@app/shared/utils';
import { distinctUntilChanged } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'manage-tags-dialog',
  templateUrl: 'manage-tags-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
})
export class ManageTagsDialog implements OnInit, OnDestroy {
  subscription = new Subscription();
  vaultTagForm: UntypedFormGroup;
  disabled = true;
  today = new Date();
  durations = [];
  years = [];
  is_inbox;
  errorMessage = '';
  newlyCreatedTag = '';
  documentTypes = [];
  customTagName = '';
  isApiCallInProgress = true;
  versions = ['Original', 'Modified'];
  searchText = '';
  filteredDocumentTypes = [];
  selectedDocumentTypes: string[] = [];
  commaSeparatedValue;
  isDropdownOpen: boolean;
  isTagInputShown: boolean = false;
  private searchSubject = new Subject<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<ManageTagsDialog>,
    private cd: ChangeDetectorRef,
    private toast: ToastrService,
    private _router: Router,
    private _userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private documentService: DocumentService
  ) {}

  ngOnInit(): void {
    this.years = this.getYearsArray();
    this.is_inbox = this._router.url.includes('inbox');
    const documentTypeArray = this.data.document_type ? this.data.document_type.split(',').map((type: string) => type.trim()) : [];
    // const formattedYear = !isNaN(Number(this.data.year)) && Number(this.data.year) ? Number(this.data.year) : '';
    this.filteredDocumentTypes = documentTypeArray;
    this.vaultTagForm = this.formBuilder.group(
      {
        // year: [formattedYear, [Validators.min(1000), Validators.required]],
        document_type: [documentTypeArray, [Validators.required]],
        // version: [this.data.version ?? ''],
      },
      { validator: atLeastOne }
    );

    this.subscription.add(
      this.searchSubject.pipe(debounceTime(100), distinctUntilChanged()).subscribe((searchTerm) => {
        this.debouncedSearch(searchTerm);
      })
    );

    this.getUserTags();
    this.cd.detectChanges();
  }

  getUserTags() {
    this.subscription.add(
      this._userService.getUserTags().subscribe(
        (res) => {
          this.documentTypes = res.map((tag) => tag.name);
          this.filteredDocumentTypes = [...new Set([...this.filteredDocumentTypes, ...this.documentTypes])].sort((a, b) => a.localeCompare(b));
          this.isApiCallInProgress = false;
          if (this.newlyCreatedTag) {
            const currentSelection = this.vaultTagForm.get('document_type')?.value || [];
            if (!currentSelection.includes(this.newlyCreatedTag)) {
              this.setValue([...currentSelection, this.newlyCreatedTag], 'document_type');
            }
            this.newlyCreatedTag = '';
          }
          this.cd.detectChanges();
        },
        (error) => {
          this.toast.error('Error occurred while getting a list of tags', error.error.message);
        }
      )
    );
  }

  addUserTags() {
    if (this.customTagName.length === 0) {
      return;
    }
    this.subscription.add(
      this._userService
        .addUserTags({
          name: this.customTagName,
          tag_type: 'user',
        })
        .subscribe(
          (data: any) => {
            if (data?.isDuplicate) {
              this.errorMessage = data?.message;
              this.vaultTagForm.get('document_type').setErrors({ duplicate: true });
              this.cd.detectChanges();
            } else if (data) {
              this.isApiCallInProgress = true;
              this.newlyCreatedTag = data?.name;
              this.getUserTags();
              this.isTagInputShown = false;
              this.customTagName = '';
              this.cd.detectChanges();
            }
          },
          (error: any) => {
            this.toast.error('Error adding custom tag', error?.error?.message || 'An error occurred');
          }
        )
    );
  }

  addTagValue() {
    if (this.vaultTagForm.valid) {
      const formValues = this.vaultTagForm.value;
      const documentTypeValue = Array.isArray(formValues.document_type) ? formValues.document_type.join(',') : formValues.document_type;
      formValues.document_type = documentTypeValue;
      // formValues.year = formValues.year.toString();
      const payload = {
        vault_user_form_uploads_id: this.data.vault_user_form_uploads_id,
        tag: JSON.stringify(formValues),
      };
      this.subscription.add(this.documentService.createVaultUserTag(payload).subscribe(() => this.dialogRef.close(true)));
    }
  }

  setValue(value, type) {
    const currentSelection = this.vaultTagForm.get('document_type')?.value || [];
    const newValue = Array.isArray(value) ? value : [value];
    const updatedSelection = [...new Set([...currentSelection, ...newValue])];
    this.vaultTagForm.get('document_type')?.setValue(updatedSelection);
  }

  setvalueTo(value, type) {}

  getYearsArray() {
    const currentYear = new Date().getFullYear();
    const startYear = 1900;
    return Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);
  }

  onInputChange(searchValue: string) {
    this.searchText = searchValue;
    if (!searchValue) {
      this.filteredDocumentTypes = this.documentTypes;
      return;
    }
    this.searchSubject.next(searchValue);
  }

  private debouncedSearch(searchTerm: string) {
    const currentSelection = this.vaultTagForm.get('document_type')?.value || [];
    if (!searchTerm) {
      this.filteredDocumentTypes = [...this.documentTypes].sort((a, b) => a.localeCompare(b));
      return;
    }
    const searchTermInLowerCase = searchTerm.toLowerCase();
    const filteredResults = this.documentTypes.filter((item) => item.toLowerCase().includes(searchTermInLowerCase));
    this.filteredDocumentTypes = [...new Set([...currentSelection, ...filteredResults])].sort((a, b) => a.localeCompare(b));
  }

  handleKeyDown(event: KeyboardEvent) {
    const keysToPrevent = [' ', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'];
    // if (event.target.selectionStart === 0 && (event.keyCode === 32 || event.keyCode === 9)) {}
    if (event.target instanceof HTMLInputElement) {
      if (event.target.selectionStart === 0 && (event.key === ' ' || event.key === 'Tab')) {
        event.preventDefault();
        return;
      }
    }
    if (keysToPrevent.includes(event.key)) {
      event.stopPropagation();
    }
  }

  selectAllContent($event) {
    $event.target.select();
  }

  onCustomTagNameInputChange(value: string) {
    this.errorMessage = '';
    this.customTagName = value;
    this.cd.detectChanges();
  }

  closeDialog(val?: string) {
    this.dialogRef.close(val);
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.subscription.unsubscribe();
  }
}
